// The per-job cost rows of the client's cost tab, grouped by the retainer they sit under.
//
// GROUPED THE WAY THE WORK TAB GROUPS. A topic's costs are drawn beneath its retainer and
// a standalone job's below, in its own section — the same shape the client card already
// presents, so the two tabs describe one structure rather than two.
//
// A TOPIC'S COSTS ARE NOT ROLLED UP into its retainer's row, and that differs from every
// profit screen in the system on purpose. Profit asks which INCOME UNIT a cost nets
// against, so a topic's cost belongs to its retainer. This tab asks where the money went,
// and the answer is the job it was spent on. The retainer is a heading here, not a total.
//
// THE THREE COLUMNS ARE NEVER SUMMED ACROSS. Absorbed, charged and unstated are three
// different kinds of money (see CostsTab), so the table shows them side by side and
// prints no row total — a "total" column would be the one figure on this screen that
// means nothing.
import he from '../../i18n/he';
import DataTable from '../../components/DataTable';
import Money, { Cost } from '../../components/Money';

const t = he.clientCard.costs;

// Zero is drawn as a dash rather than "0". Most jobs carry only one kind of cost, so a
// column of honest zeroes is noise that hides the one number on the row that matters.
// A cost column draws its figure red, as every cost on the site; the charge stays plain.
const cell = (value, As = Money) => (Number(value) > 0.005 ? <As value={value} /> : '—');

const some = (value) => Number(value) > 0.005;

// A row that holds expenses but no COST — every one of them recorded at 0.
//
// The condition is the three cost columns alone, deliberately NOT the charge beside
// them: a row with a planned charge of 1,000 and no cost is exactly the shape that hid a
// blank cost box for a whole session, and including the charge here would let the plan go
// on concealing it.
//
// THE WORDING STATES THE FACT, NOT A FAULT. A zero can be perfectly correct — goods
// received free cost us nothing — and the stored 0 is identical either way, so the row
// says what it holds and lets the reader judge. That is enough: a cost of 0 sitting in
// plain sight on the cost screen is what makes a mistyped one findable.
const noCostRecorded = (r) => r.count > 0
  && !some(r.absorbed) && !some(r.charged) && !some(r.unstated);

// Which bearers this row actually holds, named. Plural rows are rare but real — one job
// can carry an absorbed cost and a charged one — so every kind present is listed.
//
// The names come from he.expenses, the same labels the expense form and the job's own
// expense list use, rather than from this table's column headers: a header is phrased for
// a column of figures ("חויב ללקוח") and reads as a past event when it stands alone.
const bearerLabels = (counts) => [
  counts.client ? he.expenses.borneClient : null,
  counts.us ? he.expenses.borneUs : null,
  counts.unstated ? he.expenses.borneUnstated : null,
].filter(Boolean).join(' · ');

const columns = [
  {
    key: 'jobTitle',
    label: t.job,
    primary: true,
    render: (r) => (
      <>
        {r.jobTitle}
        {/* A topic bills nothing of its own, so its costs reach the client only through
            the retainer above it. Worth marking: it explains why a job with real costs
            shows no charge of its own. */}
        {r.isTopic && <span className="badge gray client-badge">{t.topic}</span>}
        {/* The row holds expenses but every one of them cost 0. Name the bearer and say
            so, rather than leaving a line of dashes that reads as nothing happened here. */}
        {noCostRecorded(r) && (
          <div className="muted small-note">
            {bearerLabels(r.counts)} — {t.zeroCost(r.count)}
          </div>
        )}
      </>
    ),
  },
  { key: 'absorbed', label: t.colAbsorbed, className: 'num', render: (r) => cell(r.absorbed, Cost) },
  { key: 'charged', label: t.colCharged, className: 'num', render: (r) => cell(r.charged, Cost) },
  // What was actually charged — or, when nothing has been yet, what we MEAN to charge,
  // drawn muted and named as a plan. Without it a cost marked for the client but not yet
  // billed shows an empty column, and the figure the partner deliberately recorded is
  // nowhere on the screen that exists to report it.
  {
    key: 'invoiced',
    label: t.colInvoiced,
    className: 'num',
    render: (r) => {
      if (some(r.invoiced)) return cell(r.invoiced);
      if (some(r.plannedCharge)) {
        return <span className="muted">{t.planned} <Money value={r.plannedCharge} /></span>;
      }
      return '—';
    },
  },
  { key: 'unstated', label: t.colUnstated, className: 'num', render: (r) => cell(r.unstated, Cost) },
];

const withIds = (rows) => rows.map((u) => ({ ...u, id: u.jobId }));

export default function UnitCostRows({ units }) {
  // Grouped here rather than on the server: it is a presentation choice, and the server
  // returning flat rows keeps the one read usable by anything that wants them ungrouped.
  const byRetainer = new Map();
  const standalone = [];
  for (const u of units) {
    if (u.retainerId == null) { standalone.push(u); continue; }
    const key = String(u.retainerId);
    if (!byRetainer.has(key)) byRetainer.set(key, { title: u.retainerTitle, rows: [] });
    byRetainer.get(key).rows.push(u);
  }

  return (
    <>
      {[...byRetainer.entries()].map(([id, group]) => (
        <div key={id} className="cost-group">
          <h3>{group.title || t.unnamedRetainer}</h3>
          <DataTable columns={columns} rows={withIds(group.rows)} />
        </div>
      ))}

      {standalone.length > 0 && (
        <div className="cost-group">
          <h3>{t.standaloneHeading}</h3>
          <DataTable columns={columns} rows={withIds(standalone)} />
        </div>
      )}
    </>
  );
}
